import { auth } from "@/lib/firebase";

type AnalyticsParameters = Record<string, unknown>;

declare global {
  interface Window {
    gtag?: (command: "event", eventName: string, parameters?: AnalyticsParameters) => void;
  }
}

const SESSION_STORAGE_KEY =
  "marginglow_product_session_id";
const CONSENT_STORAGE_KEY =
  "marginglow_privacy_consent_v1";
const GA_MEASUREMENT_ID = "G-BZV9QVJYV4";

function getProductSessionId() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const existing =
      window.localStorage.getItem(
        SESSION_STORAGE_KEY,
      );

    if (existing) {
      return existing;
    }

    const randomPart =
      typeof window.crypto?.randomUUID ===
      "function"
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}`;

    const sessionId = `mg_${randomPart}`;

    window.localStorage.setItem(
      SESSION_STORAGE_KEY,
      sessionId,
    );

    return sessionId;
  } catch {
    return `mg_${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
  }
}

async function persistFirstPartyEvent(
  eventName: string,
  parameters: AnalyticsParameters,
) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const currentUser = auth.currentUser;
    const idToken = currentUser
      ? await currentUser.getIdToken()
      : "";

    await fetch("/api/product-events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken
          ? {
              Authorization:
                `Bearer ${idToken}`,
            }
          : {}),
      },
      body: JSON.stringify({
        eventName,
        anonymousSessionId:
          getProductSessionId(),
        parameters,
        pagePath:
          window.location.pathname,
        referrerDomain: (() => {
          try {
            return document.referrer
              ? new URL(document.referrer)
                  .hostname
              : "";
          } catch {
            return "";
          }
        })(),
        clientCreatedAt:
          new Date().toISOString(),
      }),
      keepalive: true,
    });
  } catch (error) {
    console.warn(
      "MarginGlow interaction record skipped:",
      error,
    );
  }
}

export function trackEvent(
  eventName: string,
  parameters: AnalyticsParameters = {},
) {
  if (typeof window === "undefined") {
    return;
  }

  if (
    typeof window.gtag === "function"
  ) {
    window.gtag(
      "event",
      eventName,
      parameters,
    );
  }

  void persistFirstPartyEvent(
    eventName,
    parameters,
  );
}

function analyticsConsentGranted() {
  try {
    const saved = window.localStorage.getItem(
      CONSENT_STORAGE_KEY,
    );
    const parsed = saved
      ? JSON.parse(saved)
      : null;

    return (
      parsed?.version === "1" &&
      parsed?.analytics === true
    );
  } catch {
    return false;
  }
}

function gaCookieClientId() {
  const match = document.cookie.match(
    /(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/,
  );

  return match?.[1] || "";
}

function readGtagValue(
  fieldName: "client_id" | "session_id",
) {
  return new Promise<string>((resolve) => {
    if (typeof window.gtag !== "function") {
      resolve("");
      return;
    }

    let settled = false;
    const finish = (value: unknown) => {
      if (settled) {
        return;
      }

      settled = true;
      resolve(String(value || "").trim());
    };

    (window.gtag as unknown as (...args: unknown[]) => void)(
      "get",
      GA_MEASUREMENT_ID,
      fieldName,
      finish,
    );

    window.setTimeout(
      () => finish(""),
      500,
    );
  });
}

export async function getCheckoutAnalyticsContext() {
  if (
    typeof window === "undefined" ||
    !analyticsConsentGranted()
  ) {
    return {
      consent: "denied" as const,
      clientId: "",
      sessionId: "",
    };
  }

  const [gtagClientId, sessionId] =
    await Promise.all([
      readGtagValue("client_id"),
      readGtagValue("session_id"),
    ]);

  return {
    consent: "granted" as const,
    clientId:
      gtagClientId ||
      gaCookieClientId() ||
      getProductSessionId(),
    sessionId: sessionId.replace(/\D/g, ""),
  };
}
